import React from 'react';
import { parseDirectAddress } from './directAddressUtils';
import { allowsArrivalDateInput } from './shukkaDateRules';

/**
 * 送り先住所を 郵便番号／住所／電話番号 の3行に分けて表示する。
 * 基幹側で分けられない要素は行を出さず、どちらも取り出せない場合は原文を1行で出す。
 * @param {string} [maxWidthClass] 列幅の上限。幅が固定済みの印刷テーブルでは空文字を渡す
 */
export function DirectAddressCell({ address, tel, maxWidthClass = 'max-w-[17rem]' }) {
  const parsed = parseDirectAddress(address, tel);
  if (parsed.isEmpty) return '-';

  const subLineClass = 'text-[11px] text-slate-500';
  return (
    <div
      className={`text-slate-900 whitespace-normal break-words leading-snug ${maxWidthClass}`}
      title={parsed.raw}
    >
      {parsed.postalCode ? <div className={subLineClass}>〒{parsed.postalCode}</div> : null}
      {parsed.address ? <div>{parsed.address}</div> : null}
      {parsed.tel ? <div className={subLineClass}>℡ {parsed.tel}</div> : null}
    </div>
  );
}

/** セル内2行表示（スラッシュ相当） */
export function TwoLineCell({ top, bottom, topClass = 'truncate', bottomClass = 'text-slate-600 truncate' }) {
  const t = top || '-';
  const b = bottom || '-';
  return (
    <div
      className="text-slate-900 whitespace-normal break-words leading-snug"
      title={`${t}\n${b}`}
      style={{ maxHeight: '3.2rem', overflow: 'hidden' }}
    >
      <div className={topClass}>{t}</div>
      <div className={bottomClass}>{b}</div>
    </div>
  );
}

/** 出/着の縦並び1行（ラベル＋本文） */
export function LabeledShipArrivalRow({ label, children, labelTitle }) {
  return (
    <div className="flex items-center gap-1 min-w-0">
      <span
        className="shrink-0 w-3 text-[10px] font-semibold text-slate-500 leading-none"
        title={labelTitle || undefined}
      >
        {label}
      </span>
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

/** 発送日・着日の縦並び表示（読み取り） */
export function ShipArrivalDisplay({ shipText, arrivalText, hideArrival = false }) {
  const title = hideArrival ? `発 ${shipText}` : `発 ${shipText}\n着 ${arrivalText}`;
  return (
    <div className="space-y-0.5 text-slate-900 leading-snug" title={title}>
      <LabeledShipArrivalRow label="発" labelTitle="発送日">
        <span className="text-xs truncate block">{shipText}</span>
      </LabeledShipArrivalRow>
      {!hideArrival && (
        <LabeledShipArrivalRow label="着" labelTitle="着日">
          <span className="text-xs text-slate-600 truncate block">{arrivalText}</span>
        </LabeledShipArrivalRow>
      )}
    </div>
  );
}

export function formatShareDate(dateStr) {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit' });
}

export function formatShareNumber(num) {
  if (num === null || num === undefined) return '-';
  return Number(num).toLocaleString();
}

/**
 * 仕入先共有の共通列定義。
 * @param {'view'|'edit'} mode view=社内参照（日付編集可）, edit=仕入先編集（メモ等）
 * @param {object} handlers 画面固有ハンドラ
 */
export function buildSupplierShareColumns(mode, handlers = {}) {
  const isView = mode === 'view';
  const {
    formatDate = formatShareDate,
    formatNumber = formatShareNumber,
    onShipmentDateChange,
    onArrivalDateChange,
    onMemoChange,
    onHacchuNoClick,
    onClusterEdit,
    MemoCell,
    StockCell,
    ShipmentOriginCell,
    includeStock = true,
    includeShipmentOrigin = false,
  } = handlers;

  const wrapClusterEditable = (row, node) => {
    if (!isView || typeof onClusterEdit !== 'function') return node;
    return (
      <div
        role="button"
        tabIndex={0}
        className="cursor-pointer hover:bg-slate-50 rounded -mx-1 px-1"
        title="クリックして一括編集"
        onClick={(e) => {
          e.stopPropagation();
          onClusterEdit(row);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            onClusterEdit(row);
          }
        }}
      >
        {node}
      </div>
    );
  };

  const cols = [
    {
      header: '出荷形態/発注日',
      field: 'shukka_hacchu_date',
      render: (row) => <TwoLineCell top={row.shukka_type} bottom={row.hacchu_date} />,
    },
    {
      header: '受注No/発注No',
      field: 'juchu_hacchu_no',
      render: (row) => {
        if (typeof onHacchuNoClick === 'function' && row.hacchu_no) {
          return (
            <div
              className="text-slate-900 whitespace-normal break-words leading-snug"
              title={`${row.juchu_no || '-'}\n${row.hacchu_no}`}
              style={{ maxHeight: '3.2rem', overflow: 'hidden' }}
            >
              <div className="truncate">{row.juchu_no || '-'}</div>
              <button
                type="button"
                className="text-blue-600 hover:underline text-left truncate max-w-full"
                onClick={(e) => {
                  e.stopPropagation();
                  onHacchuNoClick(row.hacchu_no, row);
                }}
              >
                {row.hacchu_no}
              </button>
            </div>
          );
        }
        return <TwoLineCell top={row.juchu_no} bottom={row.hacchu_no} />;
      },
    },
    {
      header: '営業事務/営業',
      field: 'shain_pair',
      render: (row) => <TwoLineCell top={row.hacchu_shain_name} bottom={row.tanto_shain_name} />,
    },
    {
      header: '依頼日/依頼作成者',
      field: 'request_pair',
      render: (row) => {
        const issueStatus = row.ship_request_issue_status;
        const statusLabel =
          issueStatus === 'full' ? '発行済' : issueStatus === 'partial' ? '一部' : '未発行';
        const statusClass =
          issueStatus === 'full' || issueStatus === 'partial'
            ? 'text-emerald-800'
            : 'text-slate-400';
        const no = row.latest_ship_request_no || '';
        const dateText = formatDate(row.latest_ship_request_issued_at);
        const byText = row.latest_ship_request_issued_by || '';
        const second =
          dateText !== '-' || byText
            ? [dateText !== '-' ? dateText : null, byText || null].filter(Boolean).join(' / ')
            : '-';
        return (
          <div className="leading-snug space-y-0.5" title={`${statusLabel} ${no}\n${second}`}>
            <div className={`text-xs truncate ${statusClass}`}>
              <span className="font-semibold">{statusLabel}</span>
              {no ? <span className="ml-1 text-slate-700 font-normal">{no}</span> : null}
            </div>
            <div className="text-[11px] text-slate-600 truncate">{second}</div>
          </div>
        );
      },
    },
    {
      header: '得意先/納入先',
      field: 'customer_nonyusaki',
      render: (row) => <TwoLineCell top={row.customer_name} bottom={row.nonyusaki} />,
    },
    {
      header: '現場',
      field: 'genba_name',
      render: (row) => row.genba_name || '-',
    },
    {
      header: '予定納期',
      field: 'supplier_response_due_date',
      render: (row) => {
        const displayDate = row.latest_supplier_response_due_date || row.supplier_response_due_date;
        return <span className="text-slate-900">{formatDate(displayDate)}</span>;
      },
    },
    {
      header: '発送日/着日',
      field: 'ship_arrival',
      render: (row) => {
        const ship = row.latest_shipment_date || row.shipment_date;
        const arr = row.latest_arrival_date || row.arrival_date;
        const hasSplit = row.split_plans && row.split_plans.length > 0;
        const showArrival = allowsArrivalDateInput(row.shukka_type);
        if (isView && onShipmentDateChange && onArrivalDateChange && !hasSplit) {
          return (
            <div className="space-y-1">
              <LabeledShipArrivalRow label="発" labelTitle="発送日">
                <input
                  type="date"
                  aria-label="発送日"
                  className="w-full max-w-[8.5rem] px-1 py-0.5 text-xs border border-slate-300 rounded"
                  value={ship ? String(ship).slice(0, 10) : ''}
                  onChange={(e) => onShipmentDateChange(row, e.target.value || null)}
                />
              </LabeledShipArrivalRow>
              {showArrival && (
                <LabeledShipArrivalRow label="着" labelTitle="着日">
                  <input
                    type="date"
                    aria-label="着日"
                    className="w-full max-w-[8.5rem] px-1 py-0.5 text-xs border border-slate-300 rounded"
                    value={arr ? String(arr).slice(0, 10) : ''}
                    onChange={(e) => onArrivalDateChange(row, e.target.value || null)}
                  />
                </LabeledShipArrivalRow>
              )}
            </div>
          );
        }
        const splitHint = hasSplit ? `（分納${row.split_plans.length}）` : '';
        return (
          <ShipArrivalDisplay
            shipText={`${formatDate(ship)}${splitHint}`}
            arrivalText={formatDate(arr)}
            hideArrival={!showArrival}
          />
        );
      },
    },
    {
      header: '総重量',
      field: 'total_weight',
      render: (row) =>
        row.total_weight != null ? formatNumber(Number(row.total_weight).toFixed(1)) : '-',
    },
    {
      header: '製品CD',
      field: 'product_disp',
      render: (row) => row.product_disp || '-',
    },
    {
      header: '製品名/寸法',
      field: 'product_name_size',
      render: (row) => <TwoLineCell top={row.product_name} bottom={row.product_size} />,
    },
    {
      header: '数量',
      field: 'hacchu_amount',
      render: (row) => {
        if (row.hacchu_amount == null) return '-';
        const amount = formatNumber(row.hacchu_amount);
        const unit = row.tani || '';
        return unit ? `${amount} ${unit}` : amount;
      },
    },
    {
      header: '重量',
      field: 'product_weight',
      render: (row) =>
        row.product_weight != null ? formatNumber(Number(row.product_weight).toFixed(3)) : '-',
    },
    {
      header: '仕入先名',
      field: 'supplier_name',
      render: (row) => row.supplier_name || '-',
    },
    {
      header: '製品仕様',
      field: 'product_spec',
      render: (row) => row.product_spec || '-',
    },
    {
      header: '送り先',
      field: 'juchu_direct_name',
      render: (row) => row.juchu_direct_name || '-',
    },
    {
      header: '送り先住所',
      field: 'juchu_direct_address',
      render: (row) =>
        wrapClusterEditable(
          row,
          <DirectAddressCell address={row.juchu_direct_address} tel={row.juchu_direct_tel} />
        ),
    },
    {
      header: '備考',
      field: 'juchu_biko',
      render: (row) => wrapClusterEditable(row, row.juchu_biko || '-'),
    },
    {
      header: 'メモ',
      field: 'supplier_share_memo',
      render: (row) => {
        if (MemoCell) return <MemoCell row={row} />;
        if (!isView && onMemoChange) {
          return null;
        }
        return wrapClusterEditable(
          row,
          <span className="text-slate-900">{row.supplier_share_memo || '-'}</span>
        );
      },
    },
  ];

  if (includeStock && StockCell) {
    const idx = cols.findIndex((c) => c.field === 'supplier_response_due_date');
    cols.splice(idx, 0, {
      header: '在庫',
      field: 'supplier_stock_flag',
      render: (row) => <StockCell row={row} />,
    });
  }

  if (includeShipmentOrigin && ShipmentOriginCell) {
    const idx = cols.findIndex((c) => c.field === 'supplier_response_due_date');
    cols.splice(idx + 1, 0, {
      header: '発送元',
      field: 'shipment_origin',
      render: (row) => <ShipmentOriginCell row={row} />,
    });
  }

  return cols;
}

/** 印刷用の簡易列（key + render） */
export function buildSupplierSharePrintColumns(formatDate = formatShareDate, formatNumber = formatShareNumber) {
  return [
    { header: '出荷形態/発注日', key: 'shukka_hacchu_date', render: (row) => (
      <div><div>{row.shukka_type || '-'}</div><div className="text-slate-600">{row.hacchu_date || '-'}</div></div>
    )},
    { header: '受注No/発注No', key: 'juchu_hacchu_no', render: (row) => (
      <div><div>{row.juchu_no || '-'}</div><div className="text-slate-600">{row.hacchu_no || '-'}</div></div>
    )},
    { header: '営業事務/営業', key: 'shain_pair', render: (row) => (
      <div><div>{row.hacchu_shain_name || '-'}</div><div className="text-slate-600">{row.tanto_shain_name || '-'}</div></div>
    )},
    { header: '依頼日/依頼作成者', key: 'request_pair', render: (row) => {
      const issueStatus = row.ship_request_issue_status;
      const statusLabel =
        issueStatus === 'full' ? '発行済' : issueStatus === 'partial' ? '一部' : '未発行';
      const no = row.latest_ship_request_no || '';
      return (
      <div className="space-y-0.5">
        <div className="font-semibold">{statusLabel}{no ? ` ${no}` : ''}</div>
        <div>{formatDate(row.latest_ship_request_issued_at)}</div>
        <div className="text-slate-600">{row.latest_ship_request_issued_by || '-'}</div>
      </div>
      );
    }},
    { header: '得意先/納入先', key: 'customer_nonyusaki', render: (row) => (
      <div><div>{row.customer_name || '-'}</div><div className="text-slate-600">{row.nonyusaki || '-'}</div></div>
    )},
    { header: '現場', key: 'genba_name', render: (row) => row.genba_name || '-' },
    { header: '予定納期', key: 'supplier_response_due_date', render: (row) => formatDate(row.latest_supplier_response_due_date || row.supplier_response_due_date) },
    { header: '発送日/着日', key: 'ship_arrival', render: (row) => (
      <ShipArrivalDisplay
        shipText={formatDate(row.latest_shipment_date || row.shipment_date)}
        arrivalText={formatDate(row.latest_arrival_date || row.arrival_date)}
        hideArrival={!allowsArrivalDateInput(row.shukka_type)}
      />
    )},
    { header: '総重量', key: 'total_weight', render: (row) => (row.total_weight != null ? formatNumber(Number(row.total_weight).toFixed(1)) : '-') },
    { header: '製品CD', key: 'product_disp', render: (row) => row.product_disp || '-' },
    { header: '製品名/寸法', key: 'product_name_size', render: (row) => (
      <div><div>{row.product_name || '-'}</div><div className="text-slate-600">{row.product_size || '-'}</div></div>
    )},
    { header: '数量', key: 'hacchu_amount', render: (row) => {
      if (row.hacchu_amount == null) return '-';
      const amount = formatNumber(row.hacchu_amount);
      return row.tani ? `${amount} ${row.tani}` : amount;
    }},
    { header: '重量', key: 'product_weight', render: (row) => (row.product_weight != null ? formatNumber(Number(row.product_weight).toFixed(3)) : '-') },
    { header: '仕入先名', key: 'supplier_name', render: (row) => row.supplier_name || '-' },
    { header: '製品仕様', key: 'product_spec', render: (row) => row.product_spec || '-' },
    { header: '送り先', key: 'juchu_direct_name', render: (row) => row.juchu_direct_name || '-' },
    { header: '送り先住所', key: 'juchu_direct_address', render: (row) => (
      <DirectAddressCell
        address={row.juchu_direct_address}
        tel={row.juchu_direct_tel}
        maxWidthClass=""
      />
    )},
    { header: '備考', key: 'juchu_biko', render: (row) => row.juchu_biko || '-' },
    { header: 'メモ', key: 'supplier_share_memo', render: (row) => row.supplier_share_memo || '-' },
  ];
}
